import { useState, useEffect } from 'react';
import { ModelConfig } from './useModels';

interface FormValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export const useWorkflowForm = (model: ModelConfig | null) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);

  // Initialize form with default values
  useEffect(() => {
    if (model?.input_schema) {
      const initialData: Record<string, any> = {};
      
      model.input_schema.forEach((field: any) => {
        const fieldKey = field.key || field.variable_name || field.id;
        if (field.defaultValue !== undefined) {
          initialData[fieldKey] = field.defaultValue;
        }
      });
      
      setFormData(initialData);
      setFormErrors({});
      console.log('📋 [FORM] Initialized with default values:', initialData);
    }
  }, [model?.input_schema]);

  const handleInputChange = (fieldKey: string, value: any) => {
    console.log(`🔄 [FORM] Field "${fieldKey}" changed:`, value);
    
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
    
    // Clear error for this field when user starts typing
    if (formErrors[fieldKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  const validateForm = (): FormValidationResult => {
    const errors: Record<string, string> = {};
    
    if (!model?.input_schema) {
      return { isValid: false, errors: { form: 'No form configuration available' } };
    }

    // Validate each field
    model.input_schema.forEach((field: any) => {
      const fieldKey = field.key || field.variable_name || field.id;
      const value = formData[fieldKey];

      if (field.required) {
        if (value === undefined || value === null || value === '') {
          errors[fieldKey] = `${field.label} is required`;
        } else if (field.type === 'file') {
          // File validation
          if (field.multiple) {
            if (!Array.isArray(value) || value.length === 0) {
              errors[fieldKey] = `At least one ${field.label.toLowerCase()} is required`;
            }
          } else {
            if (!value || typeof value !== 'object' || !value.public_url) {
              errors[fieldKey] = `${field.label} is required`;
            }
          }
        }
      }

      // Type-specific validation
      if (value !== undefined && value !== null && value !== '') {
        if (field.type === 'number' || field.type === 'range') {
          const numValue = parseFloat(value);
          if (isNaN(numValue)) {
            errors[fieldKey] = `${field.label} must be a valid number`;
          } else {
            if (field.min !== undefined && numValue < field.min) {
              errors[fieldKey] = `${field.label} must be at least ${field.min}`;
            }
            if (field.max !== undefined && numValue > field.max) {
              errors[fieldKey] = `${field.label} must be at most ${field.max}`;
            }
          }
        }
      }
    });

    setFormErrors(errors);
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  const resetForm = () => {
    setFormData({});
    setFormErrors({});
    setIsAdvancedMode(false);
  };

  return {
    formData,
    formErrors,
    isAdvancedMode,
    setIsAdvancedMode,
    handleInputChange,
    validateForm,
    resetForm
  };
};